<script setup>
/**
 * AppMarkdown — renders Markdown written in the CMS (Strapi rich text) as styled HTML.
 *
 * Spacing comes from the styles below, not from where an editor left blank lines: a parser turns the text into
 * paragraphs, headings and lists, so two posts written slightly differently still look the same. The previous
 * regex renderer turned every line break into a <br>, which stacked blank lines on top of heading margins, printed
 * `- item` as a literal dash and read any `_` — in a URL or a file name — as italics.
 *
 * The output is sanitized: the content comes from an editor, but it is still HTML going into the page.
 */
import { computed, onMounted, ref, watch } from 'vue'
import { Marked } from 'marked'
import DOMPurify from 'dompurify'
import { escapeHtml, normalizeMarkdown, spaceLineBreaks, wrapTables } from '@/helpers/markdown'

const props = defineProps({
  source: { type: String, default: '' },
  // One line of text, no paragraphs or headings: for a sentence inside a bar, a bullet or a label.
  inline: { type: Boolean, default: false },
  // `inverse` for text on a dark background: links stay white instead of tint-1.
  tone: { type: String, default: 'default', validator: (value) => ['default', 'inverse'].includes(value) },
  // A heading that repeats this text is dropped when it opens the content — editors often start a section with
  // the post's title, which the page already prints above it.
  skipHeading: { type: String, default: '' },
  // `article`: the CDP Hub article's content rules (20 / 18 / 14px bold headings, numbered step rows, the bordered table, the code and quote boxes).
  variant: { type: String, default: 'default', validator: (value) => ['default', 'article'].includes(value) },
})

const IMAGE_URL = /\.(png|jpe?g|gif|webp|svg)(\?|#|$)/i

// The web schemes DOMPurify allows, plus `collx:`, the CollX app's own links: editors link player names to it.
const SANITIZE = {
  ADD_ATTR: ['target'],
  ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp|matrix|collx):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
}

// Each look's class, written out so the styles below are findable by name.
const TONE_CLASS = { default: 'app-markdown--default', inverse: 'app-markdown--inverse' }
const VARIANT_CLASS = { default: '', article: 'app-markdown--article' }

// A private instance, so these rules don't leak into any other use of `marked`.
const markdown = new Marked({
  gfm: true,
  // A single line break stays a line break, as it was under the old renderer: posts already rely on it.
  breaks: true,
  renderer: {
    link ({ href, title, tokens }) {
      const text = this.parser.parseInline(tokens)
      const titleAttr = title ? ` title="${title}"` : ''

      // `[alt](image.png)` was how editors placed an image inline before the images field existed.
      if (IMAGE_URL.test(href)) return `<img src="${href}" alt="${text}"${titleAttr}>`

      const external = /^https?:\/\//.test(href)
      return `<a href="${href}"${titleAttr}${external ? ' target="_blank" rel="noopener noreferrer"' : ''}>${text}</a>`
    },

    // A list that carries on from an earlier one (`3.` after a paragraph) tells the article's step markers where to start.
    list (token) {
      const body = token.items.map((item) => this.listitem(item)).join('')
      const tag = token.ordered ? 'ol' : 'ul'
      const start = token.ordered && token.start !== 1 ? ` start="${token.start}" style="counter-reset: step ${token.start - 1}"` : ''
      return `<${tag}${start}>\n${body}</${tag}>\n`
    },

    // A paragraph of nothing but bold lines is a heading the editor wrote in bold; the article variant spaces it like one.
    paragraph ({ tokens }) {
      const lead = tokens.some((token) => token.type === 'strong')
        && tokens.every((token) => token.type === 'strong' || token.type === 'br' || (token.type === 'text' && !token.text.trim()))
      return `<p${lead ? ' class="app-markdown__lead"' : ''}>${this.parser.parseInline(tokens)}</p>\n`
    },

    // A fenced block names its language (` ```json `), which the box prints as a label.
    code ({ text, lang, escaped }) {
      const language = (lang ?? '').match(/^[\w-]+/)?.[0].toLowerCase() ?? ''
      const code = escaped ? text : escapeHtml(text)
      return `<pre${language ? ` data-lang="${language}"` : ''}><code${language ? ` class="language-${language}"` : ''}>${code}\n</code></pre>\n`
    },
  },
})

const normalize = (text) => text.replace(/\s+/g, ' ').trim().toLowerCase()

const root = ref(null)

const html = computed(() => {
  // The editor's slips (emphasis closed after a space, `##Heading`, `• bullets`) are put right before parsing, or they print as written.
  const source = normalizeMarkdown(props.source ?? '')

  if (!source.trim()) return ''

  if (props.inline) return DOMPurify.sanitize(markdown.parseInline(source), SANITIZE)

  const tokens = markdown.lexer(source)
  const first = tokens.find((token) => token.type !== 'space')

  if (props.skipHeading && first?.type === 'heading' && normalize(first.text) === normalize(props.skipHeading)) {
    tokens.splice(tokens.indexOf(first), 1)
  }

  const output = wrapTables(markdown.parser(tokens))

  // The article's line breaks carry 8px of air: editors stack a name, its subtext and the copy on single lines.
  return DOMPurify.sanitize(props.variant === 'article' ? spaceLineBreaks(output) : output, SANITIZE)
})

// A picture taller than twice its width (a long table) is marked once its size is known, so the article's height cap leaves it alone.
const markTallImages = () => {
  if (props.variant !== 'article' || !root.value) return
  root.value.querySelectorAll('img').forEach((image) => {
    const mark = () => image.classList.toggle('app-markdown__image--tall', image.naturalHeight > image.naturalWidth * 2)
    if (image.complete && image.naturalWidth) mark()
    else image.addEventListener('load', mark, { once: true })
  })
}

onMounted(markTallImages)

// New HTML means new picture nodes.
watch(html, markTallImages, { flush: 'post' })
</script>

<template>
  <span v-if="inline" class="app-markdown" :class="TONE_CLASS[tone]" v-html="html" />
  <div v-else-if="html" ref="root" class="app-markdown" :class="[TONE_CLASS[tone], VARIANT_CLASS[variant]]" v-html="html" />
</template>

<style scoped>
.app-markdown :deep(> :first-child) { margin-top: 0; }
.app-markdown :deep(> :last-child) { margin-bottom: 0; }

.app-markdown :deep(h1) { @apply text-2xl font-bold mt-8 mb-3; }
.app-markdown :deep(h2) { @apply text-xl font-semibold mt-6 mb-2; }
.app-markdown :deep(h3) { @apply text-lg font-semibold mt-5 mb-2; }
.app-markdown :deep(:is(h4, h5, h6)) { @apply text-base font-semibold mt-4 mb-1; }

.app-markdown :deep(p) { @apply mb-3; }
.app-markdown :deep(:is(ul, ol)) { @apply mb-3 pl-5 flex flex-col gap-y-1; }
.app-markdown :deep(ul) { @apply list-disc; }
.app-markdown :deep(ol) { @apply list-decimal; }
.app-markdown :deep(li > :is(ul, ol)) { @apply mt-1 mb-0; }

.app-markdown :deep(strong) { @apply font-semibold; }
/* Only a link that still has somewhere to go looks like one: sanitising drops any other scheme's `href`. */
.app-markdown :deep(a[href]) { @apply underline text-tint-1; }
.app-markdown--inverse :deep(a[href]) { @apply text-white; }
/* A CollX app link opens the app on a phone or tablet; on a desktop it can do nothing, so it reads as text there. */
.app-markdown :deep(a[href^="collx:"]) { @apply no-touch:pointer-events-none no-touch:text-inherit no-touch:no-underline; }

.app-markdown :deep(img) { @apply w-full rounded-xl my-4; }
.app-markdown :deep(blockquote) { @apply mb-3 border-l-2 border-gray-200 pl-3 text-gray-600; }
.app-markdown :deep(code) { @apply rounded bg-gray-100 px-1 text-[0.9em]; }
.app-markdown :deep(hr) { @apply my-6 border-gray-200; }
.app-markdown :deep(.app-markdown__table) { @apply mb-3 overflow-x-auto; }

/* `article`: the CDP Hub article's content rules, over the defaults above. A long address breaks rather than widening the column. */
.app-markdown--article { @apply break-words; }
/* Phones and tablets step the two larger headings and the gaps down, as the design's mobile frame does. */
.app-markdown--article :deep(:is(h1, h2)) { @apply mt-8 mb-3 font-bold text-gray-900 touch:max-xl:mt-6; }
.app-markdown--article :deep(h1) { @apply text-xl touch:max-xl:text-lg; }
.app-markdown--article :deep(h2) { @apply text-lg touch:max-xl:text-base; }
.app-markdown--article :deep(:is(h3, h4, h5, h6)) { @apply mt-5 mb-3 text-sm font-bold text-gray-900; }
/* Paragraphs sit 8px apart, like the line breaks inside them; the blocks the design draws sit 20px from whatever is around them. */
.app-markdown--article :deep(p) { @apply mb-2; }
/* A bold line standing alone is a heading the editor wrote in bold: spaced like a 14px heading, 20px above and 12px below. */
.app-markdown--article :deep(.app-markdown__lead) { @apply mt-5 mb-3 text-gray-900; }
.app-markdown--article :deep(:is(ul, ol, blockquote, pre, .app-markdown__table)) { @apply mt-5 mb-5 touch:max-xl:mt-4 touch:max-xl:mb-4; }
.app-markdown--article :deep(li > p:last-child) { @apply mb-0; }
.app-markdown--article :deep(.app-markdown__break) { @apply block h-2; }
/* A paragraph of stacked lines (a name, its subtext, its copy) is a block of its own: 20px from the paragraphs around it, 12px under a heading like any copy. */
.app-markdown--article :deep(p:has(.app-markdown__break)) { @apply mb-5 touch:max-xl:mb-4; }
.app-markdown--article :deep(p + p:has(.app-markdown__break)) { @apply mt-5 touch:max-xl:mt-4; }

/* A picture keeps its shape under 360px tall, centred like the video block; a tall one (a long table) keeps its full height at its own width, or it would shrink to a sliver. */
.app-markdown--article :deep(img) { @apply mx-auto block w-auto max-w-full; }
.app-markdown--article :deep(img:not(.app-markdown__image--tall)) { @apply max-h-[360px]; }

/* Numbered steps: the number in a round marker beside the text, centred on it; the text flows as a block, so bold words and links stay in line. */
.app-markdown--article :deep(ol) { @apply list-none gap-y-3 pl-0; counter-reset: step; }
.app-markdown--article :deep(ol > li) { @apply relative py-1 pl-10; counter-increment: step; }
.app-markdown--article :deep(ol > li::before) { @apply absolute left-0 top-1/2 flex size-[26px] -translate-y-1/2 items-center justify-center rounded-full bg-gray-50 text-sm font-semibold text-gray-700; content: counter(step); }

.app-markdown--article :deep(.app-markdown__table) { @apply rounded-lg border border-gray-200; }
.app-markdown--article :deep(table) { @apply w-full border-collapse text-sm; }
.app-markdown--article :deep(th) { @apply bg-gray-50 px-3 py-2.5 font-semibold text-gray-500; }
/* A column aligned in the Markdown (`---:`) keeps its alignment; the rest start left. */
.app-markdown--article :deep(th:not([align])) { @apply text-left; }
.app-markdown--article :deep(td) { @apply border-t border-gray-200 px-3 py-4 text-gray-900; }

.app-markdown--article :deep(pre) { @apply rounded-lg bg-gray-50 p-4; }
.app-markdown--article :deep(pre[data-lang]::before) { @apply mb-3 block text-xs font-semibold uppercase text-gray-900; content: attr(data-lang); }
.app-markdown--article :deep(pre code) { @apply block overflow-x-auto rounded bg-white px-2 py-1 font-mono text-sm leading-[1.8] text-gray-900; }

/* A quote, with its attribution as a last line of its own. */
.app-markdown--article :deep(blockquote) { @apply flex flex-col gap-y-3 rounded-lg border-l-4 border-tint-1 bg-gray-50 px-4 py-3.5 text-sm text-gray-700; }
.app-markdown--article :deep(blockquote > p) { @apply mb-0 font-semibold; }
/* A bold line inside a quote is the quote, not a heading. */
.app-markdown--article :deep(blockquote > .app-markdown__lead) { @apply my-0 text-gray-700; }
.app-markdown--article :deep(blockquote > p:last-child:not(:first-child)) { @apply font-normal text-gray-500; }
</style>
